import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { OPERATING_MODES, OPERATING_MODE_LABELS } from '../constants/operatingModes';
import { updateOperatingMode } from '../services/terminalesService';
import EstadoModo from './EstadoModo';

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all', textAlign: 'right', ml: 2 }}>
      {value}
    </Typography>
  </Box>
);

const ModalCambiarModo = ({ open, terminal, empresa, onClose, onSuccess }) => {
  const [nuevoModo, setNuevoModo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [severidad, setSeveridad] = useState('success');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    if (open && terminal) {
      setNuevoModo(terminal.operating_mode || '');
    }
  }, [open, terminal]);

  const handleClose = () => {
    if (guardando) return;
    onClose();
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnack(false);
  };

  const confirmar = async (e) => {
    e.preventDefault();
    if (!terminal || !nuevoModo) return;

    setGuardando(true);
    try {
      const response = await updateOperatingMode(empresa, terminal.id, nuevoModo);
      setSeveridad('success');
      setMensaje('El modo de operación se actualizó correctamente');
      setOpenSnack(true);
      onSuccess?.(response.data || { id: terminal.id, operating_mode: nuevoModo });
      setTimeout(() => {
        setGuardando(false);
        onClose();
      }, 900);
    } catch (error) {
      setSeveridad('error');
      setMensaje(error.message || 'Error al actualizar el modo de operación');
      setOpenSnack(true);
      setGuardando(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Configurar terminal
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={guardando} sx={{ color: 'inherit' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={confirmar}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2, mb: 3 }}>
              <InfoRow label="Terminal" value={terminal?.id} />
              <Divider sx={{ my: 0.5 }} />
              <InfoRow label="Store ID" value={terminal?.store_id} />
              <Divider sx={{ my: 0.5 }} />
              <InfoRow label="POS ID" value={terminal?.pos_id} />
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  Modo actual
                </Typography>
                <EstadoModo mode={terminal?.operating_mode} />
              </Box>
            </Box>

            <FormControl fullWidth size="small" required>
              <InputLabel>Nuevo modo</InputLabel>
              <Select
                label="Nuevo modo"
                value={nuevoModo}
                onChange={(e) => setNuevoModo(e.target.value)}
              >
                {OPERATING_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {OPERATING_MODE_LABELS[mode]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
              <Button color="inherit" onClick={handleClose} disabled={guardando}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disableElevation
                disabled={guardando || !nuevoModo || nuevoModo === terminal?.operating_mode}
              >
                {guardando ? 'Guardando...' : 'Confirmar'}
              </Button>
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      <Stack spacing={2} sx={{ width: '100%' }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={2500}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnack} severity={severidad} variant="filled" sx={{ width: '100%' }}>
            {mensaje}
          </Alert>
        </Snackbar>
      </Stack>
    </>
  );
};

export default ModalCambiarModo;
